import { Alert, Button, Form, Input, Checkbox } from 'antd';
import { LoginFormData } from '@/types/TLogin';
import './ReceptionistLoginForm.scss';
import { Link, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { userLogin } from 'src/api/user';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { setAccount } from '@/store/account/slice';
import LocalStorageUtility from '@/utils/localStorage';
import { LocalStorageKeys } from 'src/enums/ELocalStorage';
import PublicLayout from '@/components/Layout/PublicLayout';
import { getResellerByCode, getResellerByDomain } from '@/api/unbrandedReceptionist';
import { useEffect, useMemo, useState } from 'react';
import { LoginForm } from '@/components/Form/LoginForm/LoginForm';
import ReCaptchaVerification from '@/components/ReCaptchaVerification';
import {
	APP_LANDING_PAGE,
	AppRoutes,
	BOOK_CONSULTATION_LANDING_PAGE,
	BRANDED_DOMAINS,
	PRIVACY_POLICY_LANDING_PAGE,
	TERMS_OF_USE_LANDING_PAGE,
	UNBRANDED_DOMAINS,
} from 'src/enums/ERoutes';
import ResellerImage from '../ResellerLogin/ResellerImage';
import { CheckCircle } from 'lucide-react';
import { UserAccount } from '@/types/TAccounts';
import { RegistrationType } from 'src/enums/ERegistrationType';
import { USER_TYPES } from 'src/enums/EUser';
import { updatePWADisplay } from '@/utils/pwaHelper';

export const ReceptionistLoginForm: React.FC = () => {
	const items = [
		{ key: 1, target: '_blank', url: APP_LANDING_PAGE, label: 'Product' },
		{ key: 2, target: '_self', url: AppRoutes.RECEPTIONIST_LOGIN, label: 'Login' },
		{ key: 3, target: '_self', url: AppRoutes.REGISTER, label: 'Register' },
		{ key: 4, target: '_blank', url: BOOK_CONSULTATION_LANDING_PAGE, label: 'Book Consultation' },
		{ key: 5, target: '_blank', url: APP_LANDING_PAGE, label: 'Contact' },
		{ key: 6, target: '_blank', url: PRIVACY_POLICY_LANDING_PAGE, label: 'Privacy Policy' },
		{ key: 7, target: '_blank', url: TERMS_OF_USE_LANDING_PAGE, label: 'Terms of Use' },
	];

	const [form] = Form.useForm<LoginFormData>();
	const [isVerified, setIsVerified] = useState(false);
	const [shouldVerify, setShouldVerify] = useState(false);
	const dispatch = useAppDispatch();
	const location = useLocation();
	const navigate = useNavigate();
	const outletContext = useOutletContext<any>();
	const {
		from,
		debrand: debrandFromLocation,
		resellerCode: resellerCodeFromLocation,
	} = (location.state as {
		from: string;
		debrand: boolean;
		resellerCode: string;
	}) || {
		from: '/',
		debrand: false,
	};

	const searchParams = new URLSearchParams(location.search);
	const resellerCode = resellerCodeFromLocation || searchParams.get('resellerCode') || '';

	const hostname = window.location.hostname.replace(/^www\./, '');
	const isCustomDomain =
		!BRANDED_DOMAINS.includes(hostname) && !UNBRANDED_DOMAINS.includes(hostname);

	const debrand = outletContext?.debrand || debrandFromLocation || isCustomDomain || !!resellerCode;

	const mutation = useMutation({
		mutationKey: ['loginApi'],
		mutationFn: userLogin,
		onSuccess(data: any) {
			LocalStorageUtility.setLocalData(LocalStorageKeys.AUTH_TOKEN, data.token);

			if (data.celloToken && data.data?.userType === USER_TYPES.CLIENT) {
				LocalStorageUtility.setLocalData(LocalStorageKeys.CELLO_TOKEN, data.celloToken);
			}
			dispatch(
				setAccount({
					isAuthenticated: true,
					userAccount: data.data as UserAccount,
					authToken: data.token as string,
					currentPage: '',
					activeConfigureTab: '',
					agents: [],
					selectedAgentId: null,
					agentPreview: false,
					user: null,
					crmGroups: null,
				}),
			);
			navigate(debrand && from === '/' ? AppRoutes.UNBRANDED_DASHBOARD : from, { replace: true });
		},
	});

	const handleSubmit = (values: LoginFormData) => {
		if (!isVerified) {
			setShouldVerify(true);
			return;
		}
		mutation.mutate({ ...values, from: USER_TYPES.CLIENT });
	};

	const filteredItems = useMemo(
		() =>
			items.filter((item) => {
				if (location.pathname === AppRoutes.RECEPTIONIST_LOGIN && item.label === 'Login') {
					return false;
				}
				if (location.pathname === AppRoutes.REGISTER && item.label === 'Register') {
					return false;
				}
				return true;
			}),
		[location.pathname],
	);

	const { data } = useQuery({
		queryKey:
			isCustomDomain && !resellerCode
				? ['reseller-domain', hostname]
				: ['reseller-code', resellerCode],
		queryFn: () =>
			isCustomDomain && !resellerCode
				? getResellerByDomain({ domain: hostname })
				: getResellerByCode({ resellerCode }),
		enabled: isCustomDomain || !!resellerCode,
	});

	useEffect(() => {
		if (typeof window !== 'undefined' && data?.reseller?.reseller_name) {
			try {
				const title = debrand ? `${data?.reseller.reseller_name} | Login` : 'Frontdesk | Login';

				// Update the document title (tab title)
				document.title = title;

				// Update the meta description
				const metaDescription = document.querySelector('meta[name="description"]');
				if (metaDescription) {
					metaDescription.setAttribute('content', title);
				}

				// If you still need to update an element with id 'page-title'
				const pageTitle = document.getElementById('page-title');
				if (pageTitle) {
					pageTitle.textContent = title;
				}
			} catch (err) {
				console.error('Error updating page metadata:', err);
			}
		}

		if (data?.reseller) {
			updatePWADisplay();
		}
	}, [data?.reseller, debrand]);

	const baseClass = 'login-form-container';

	const FormItems = () => (
		<>
			{debrand && (
				<ResellerImage
					debrand={debrand}
					resellerCode={resellerCode}
					customDomain={isCustomDomain ? hostname : undefined}
				/>
			)}

			<Form.Item className={`${baseClass}__heading`}>
				<h2>Welcome Back</h2>
				<span>Log in to view all your call logs and messages</span>
			</Form.Item>
			<Form.Item
				label="Username"
				name="username"
				className={`${baseClass}__input-item`}
				rules={[
					{
						required: true,
						message: 'Username is required!',
					},
				]}
			>
				<Input placeholder="Username" />
			</Form.Item>
			<Form.Item
				className={`${baseClass}__password-input-item`}
				name="password"
				label="Password"
				rules={[
					{
						required: true,
						message: 'Password is required!',
					},
				]}
			>
				<Input.Password placeholder="Password" type={debrand ? 'text' : 'password'} />
			</Form.Item>

			<Form.Item>
				<div className="max-w-[342px]">
					{!shouldVerify && !isVerified ? (
						<div className="bg-gray-50 p-3 rounded-md border hover:bg-gray-100 transition-colors">
							<Checkbox
								onChange={(e) => {
									if (e.target.checked) {
										setShouldVerify(true);
									}
								}}
								className="flex items-center"
							>
								<span className="text-gray-600 text-sm font-semibold">Verify you are human</span>
							</Checkbox>
						</div>
					) : shouldVerify ? (
						<div className="text-gray-500 flex items-center justify-center gap-2 p-4">
							<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-500" />
							<span>Verifying you're human...</span>
						</div>
					) : isVerified ? (
						<div className="flex items-center gap-2 text-green-600 bg-green-50 p-4 rounded-lg">
							<CheckCircle className="h-5 w-5" />
							<span className="font-medium">Human verification completed.</span>
						</div>
					) : null}
				</div>
				<ReCaptchaVerification
					trigger={shouldVerify}
					onVerificationComplete={(success) => {
						setIsVerified(success);
						setShouldVerify(false);
					}}
					className="hidden"
				/>
			</Form.Item>

			<Form.Item>
				<Button
					loading={mutation.status === 'pending'}
					htmlType="submit"
					className={`${baseClass}__submit-button`}
					disabled={shouldVerify || !isVerified}
				>
					{shouldVerify ? 'Verifying...' : 'Submit'}
				</Button>
			</Form.Item>
			{mutation.isError && (
				<Form.Item className={`${baseClass}__error-response`}>
					<Alert message={mutation.error?.message} type="error" />
				</Form.Item>
			)}
			{debrand ? (
				isCustomDomain && (
					<Form.Item className={`${baseClass}__footer`}>
						<span className="register-now">
							Don't have an account?{' '}
							<a
								className="cursor-pointer"
								href={
									data?.reseller?.defaultRegistration === RegistrationType.PAID
										? AppRoutes.GENERIC_DOMAIN_REGISTER
										: AppRoutes.UNBRANDED_CLIENT_TRIAL
								}
							>
								Register Now
							</a>
						</span>
					</Form.Item>
				)
			) : (
				<Form.Item className={`${baseClass}__footer`}>
					<span className="register-now">
						Don't have an account?{' '}
						{isCustomDomain ? (
							<a
								className="cursor-pointer"
								href={
									data?.reseller?.defaultRegistration === RegistrationType.PAID
										? AppRoutes.GENERIC_DOMAIN_REGISTER
										: AppRoutes.UNBRANDED_CLIENT_TRIAL
								}
							>
								Register Now
							</a>
						) : (
							<Link to={AppRoutes.REGISTER}>Register Now</Link>
						)}
					</span>
					<br />
					<span className="forgot-password">
						<Link to={AppRoutes.FORGOT_PASSWORD}>Forgot Password?</Link>
					</span>
				</Form.Item>
			)}

			{debrand && (data?.reseller?.privacyPolicyURL || data?.reseller?.termsOfUseURL) && (
				<div className=" text-sm text-center">
					Please check our{' '}
					{debrand && data?.reseller?.privacyPolicyURL && (
						<Link
							to={data?.reseller?.privacyPolicyURL}
							className="text-blue-600 font-medium hover:font-semibold"
						>
							Privacy Policy
						</Link>
					)}
					{(debrand && data && data?.reseller?.termsOfUseURL) || !debrand ? (
						<>
							{debrand &&
								data?.reseller?.privacyPolicyURL &&
								data?.reseller?.termsOfUseURL &&
								' & '}
							{debrand && data?.reseller?.termsOfUseURL && (
								<Link
									to={data?.reseller?.termsOfUseURL}
									className="text-blue-600 font-medium hover:font-semibold"
								>
									Terms of Use
								</Link>
							)}
						</>
					) : null}
				</div>
			)}
		</>
	);

	return (
		<PublicLayout debrand={debrand} items={filteredItems}>
			<LoginForm
				form={form}
				customClassName="!my-auto"
				formItems={<FormItems />}
				handleSubmit={handleSubmit}
				autoComplete={debrand ? 'off' : 'on'}
			/>
		</PublicLayout>
	);
};
